import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import {
  ArrowLeft, Rocket, Globe2, Lock, Camera, Upload, Trash2, Link2, ExternalLink,
  Loader2, ImageIcon, Film, Tag as TagIcon, Plus, X, Save, Eye, Sparkles, Clapperboard, Check,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import GameCanvas from "@/components/GameCanvas";
import TrailerButton from "@/components/TrailerButton";
import { Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["Platformer", "Racing", "Puzzle", "Shooter", "Adventure", "Quiz", "Educational", "Card", "Reaction", "Funny", "Kids", "School Projects"];
const AGES = ["Everyone", "6+", "9+", "12+", "Teen"];
const FEATURE_IDEAS = ["Single player", "Local co-op", "Controller support", "Original soundtrack", "Boss battles", "Open world", "Story mode", "Speedrun friendly"];

const ytEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};
const imgUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

export default function PublishHub() {
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const genTrailerRef = useRef(null); // one-tap trailer generator exposed by TrailerButton
  const [trailerPrompt, setTrailerPrompt] = useState(false); // publish-time "generate trailer?" prompt

  const [game, setGame] = useState(null);
  const [sp, setSp] = useState({ headline: "", tagline: "", trailer_url: "", trailer_clip_url: "", features: [], screenshots: [] });
  const [meta, setMeta] = useState({ description: "", category: "Adventure", age_rating: "Everyone", tags: [], visibility: "public" });
  const [featureInput, setFeatureInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(`/games/${id}`).then(({ data }) => {
      setGame(data);
      const s = data.store_page || {};
      setSp({ headline: s.headline || "", tagline: s.tagline || "", trailer_url: s.trailer_url || "", trailer_clip_url: s.trailer_clip_url || "", features: s.features || [], screenshots: s.screenshots || [] });
      setMeta({
        description: data.description || "", category: data.category || "Adventure",
        age_rating: data.age_rating || "Everyone", tags: data.tags || [], visibility: data.visibility || "public",
      });
    }).catch(() => { toast.error("Could not load this game."); navigate("/app/games"); });
  }, [id, navigate]);

  const captureShot = async () => {
    if (sp.screenshots.length >= 8) { toast.error("Up to 8 screenshots."); return; }
    setBusy("capture");
    try {
      const dataUrl = await canvasRef.current?.capture();
      if (!dataUrl) throw new Error("no frame");
      const { data } = await api.post(`/games/${id}/screenshots`, { image: dataUrl });
      setSp((s) => ({ ...s, screenshots: data.screenshots }));
      toast.success("Screenshot captured! 📸");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't capture — let the preview load first."); }
    finally { setBusy(""); }
  };

  const uploadShot = async (file) => {
    if (!file) return;
    setBusy("upload");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post(`/games/${id}/screenshots`, { image: reader.result });
        setSp((s) => ({ ...s, screenshots: data.screenshots }));
        toast.success("Screenshot added!");
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setBusy(""); }
    };
    reader.readAsDataURL(file);
  };

  const removeShot = async (url) => {
    try {
      const { data } = await api.delete(`/games/${id}/screenshots`, { params: { url } });
      setSp((s) => ({ ...s, screenshots: data.screenshots }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addFeature = (val) => {
    const v = (val || featureInput).trim();
    if (!v || sp.features.includes(v) || sp.features.length >= 8) return;
    setSp((s) => ({ ...s, features: [...s.features, v] }));
    setFeatureInput("");
  };
  const addTag = () => {
    const v = tagInput.trim();
    if (!v || meta.tags.includes(v) || meta.tags.length >= 8) return;
    setMeta((m) => ({ ...m, tags: [...m.tags, v] }));
    setTagInput("");
  };

  const storePayload = () => ({ headline: sp.headline, tagline: sp.tagline, trailer_url: sp.trailer_url, features: sp.features });

  const saveDraft = async () => {
    setBusy("save");
    try {
      await api.patch(`/games/${id}/store-page`, { ...storePayload(), tags: meta.tags, visibility: meta.visibility, age_rating: meta.age_rating });
      toast.success("Store page saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      let cover = null;
      if (!game.cover_image) {
        try { cover = await canvasRef.current?.capture(); } catch { /* ignore */ }
      }
      const { data } = await api.post(`/games/${id}/publish`, {
        description: meta.description, category: meta.category, tags: meta.tags,
        visibility: meta.visibility, age_rating: meta.age_rating, store_page: storePayload(),
        cover_image: cover,
      });
      if (data.status === "published") confetti({ particleCount: 160, spread: 80, origin: { y: 0.6 } });
      toast.success(data.message);
      setGame((g) => ({ ...g, status: data.status, visibility: data.visibility || meta.visibility }));
      // Nudge 3D creators to add a cinematic trailer so their game pops in Discover.
      if (data.status === "published" && game.config?.dimension === "3d" && !sp.trailer_clip_url) {
        setTimeout(() => setTrailerPrompt(true), 900);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const shareLink = `${window.location.origin}/app/arcade/${id}`;
  const copyLink = () => { navigator.clipboard?.writeText(shareLink); toast.success("Share link copied!"); };

  const removeTrailerClip = async () => {
    try { await api.delete(`/games/${id}/trailer`); setSp((s) => ({ ...s, trailer_clip_url: "" })); toast.success("Cinematic trailer removed."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't remove the trailer."); }
  };

  if (!game) return <Spinner />;
  const isPublished = game.status === "published";
  const trailer = ytEmbed(sp.trailer_url);
  const is3D = game.config?.dimension === "3d";

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="publish-hub">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${id}`)} data-testid="ph-back"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-extrabold truncate flex items-center gap-2"><Rocket className="w-6 h-6 text-[hsl(var(--primary))]" /> Publishing Hub</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] truncate">{game.cover_emoji} {game.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isPublished && <Button variant="outline" className="rounded-full" onClick={() => navigate(`/app/arcade/${id}`)} data-testid="ph-view-page"><Eye className="w-4 h-4" /> View page</Button>}
          <Button variant="outline" className="rounded-full" onClick={saveDraft} disabled={!!busy} data-testid="ph-save-btn">
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </Button>
          <Button className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" onClick={publish} disabled={!!busy} data-testid="ph-publish-btn">
            {busy === "publish" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} {isPublished ? "Update release" : "Publish"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT — settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Release */}
          <Section title="Release" icon={Globe2}>
            <div className="grid sm:grid-cols-2 gap-3">
              <button onClick={() => setMeta((m) => ({ ...m, visibility: "public" }))} data-testid="vis-public"
                className={`text-left rounded-xl border p-3.5 transition-all ${meta.visibility === "public" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))]"}`}>
                <div className="font-bold flex items-center gap-1.5"><Globe2 className="w-4 h-4 text-[hsl(var(--primary))]" /> Public release</div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Listed in the Arcade so anyone can discover and play it.</p>
              </button>
              <button onClick={() => setMeta((m) => ({ ...m, visibility: "unlisted" }))} data-testid="vis-unlisted"
                className={`text-left rounded-xl border p-3.5 transition-all ${meta.visibility === "unlisted" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))]"}`}>
                <div className="font-bold flex items-center gap-1.5"><Lock className="w-4 h-4 text-amber-500" /> Private test link</div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Unlisted — only people with the link can play. Great for playtesting.</p>
              </button>
            </div>
            {isPublished && (
              <div className="mt-3 flex items-center gap-2 rounded-xl bg-[hsl(var(--secondary))]/40 p-2.5">
                <Link2 className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />
                <span className="text-xs truncate flex-1 font-mono">{shareLink}</span>
                <Button size="sm" variant="ghost" className="h-7 rounded-full shrink-0" onClick={copyLink} data-testid="ph-copy-link"><Link2 className="w-3.5 h-3.5" /> Copy</Button>
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-3 mt-4">
              <Field label="Category">
                <Select value={meta.category} onValueChange={(v) => setMeta((m) => ({ ...m, category: v }))}>
                  <SelectTrigger data-testid="ph-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Age rating">
                <Select value={meta.age_rating} onValueChange={(v) => setMeta((m) => ({ ...m, age_rating: v }))}>
                  <SelectTrigger data-testid="ph-age"><SelectValue /></SelectTrigger>
                  <SelectContent>{AGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          {/* Store page copy */}
          <Section title="Store page" icon={Sparkles}>
            <div className="space-y-3">
              <Field label="Headline">
                <Input value={sp.headline} maxLength={120} placeholder="One punchy line — e.g. Outrun the storm across floating islands"
                  onChange={(e) => setSp((s) => ({ ...s, headline: e.target.value }))} data-testid="ph-headline" />
              </Field>
              <Field label="Tagline">
                <Input value={sp.tagline} maxLength={200} placeholder="A short hook shown under the title"
                  onChange={(e) => setSp((s) => ({ ...s, tagline: e.target.value }))} data-testid="ph-tagline" />
              </Field>
              <Field label="About this game">
                <Textarea value={meta.description} rows={4} maxLength={500} placeholder="Tell players what your game is about…"
                  onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))} data-testid="ph-description" />
              </Field>
              <Field label="Trailer (YouTube URL)">
                <Input value={sp.trailer_url} placeholder="https://youtube.com/watch?v=…"
                  onChange={(e) => setSp((s) => ({ ...s, trailer_url: e.target.value }))} data-testid="ph-trailer" />
                {sp.trailer_url && !trailer && <p className="text-[11px] text-amber-500 mt-1">Enter a valid YouTube link to embed a trailer.</p>}
                {is3D && (
                  <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="ph-autotrailer">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-amber-500 flex items-center gap-1.5"><Clapperboard className="w-3.5 h-3.5" /> Auto-trailer</div>
                        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">Record a ~7-second cinematic flythrough of your 3D world — no editing needed.</p>
                      </div>
                      <TrailerButton canvasRef={canvasRef} gameId={id} title={game.title} registerGenerate={(fn) => { genTrailerRef.current = fn; }} onSaved={(url) => setSp((s) => ({ ...s, trailer_clip_url: url }))} />
                    </div>
                    {sp.trailer_clip_url && (
                      <div className="flex items-center justify-between gap-2 mt-2.5 text-[11px]">
                        <span className="text-emerald-500 font-semibold flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Cinematic trailer set</span>
                        <button onClick={removeTrailerClip} className="text-[hsl(var(--muted-foreground))] hover:text-red-500 underline" data-testid="ph-remove-trailer">Remove</button>
                      </div>
                    )}
                  </div>
                )}
              </Field>
            </div>
          </Section>

          {/* Features */}
          <Section title="Feature tags" icon={TagIcon}>
            <div className="flex gap-2">
              <Input value={featureInput} placeholder="Add a feature… e.g. Boss battles" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())} onChange={(e) => setFeatureInput(e.target.value)} data-testid="ph-feature-input" />
              <Button variant="outline" className="rounded-full shrink-0" onClick={() => addFeature()} data-testid="ph-feature-add"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {FEATURE_IDEAS.filter((f) => !sp.features.includes(f)).map((f) => (
                <button key={f} onClick={() => addFeature(f)} className="text-[11px] px-2 py-1 rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]/20 font-medium">+ {f}</button>
              ))}
            </div>
            {sp.features.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="ph-features">
                {sp.features.map((f) => (
                  <Badge key={f} variant="secondary" className="gap-1 pr-1">{f}
                    <button onClick={() => setSp((s) => ({ ...s, features: s.features.filter((x) => x !== f) }))}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
            <Label className="mt-4 mb-1.5 block text-xs">Discovery tags</Label>
            <div className="flex gap-2">
              <Input value={tagInput} placeholder="Add a tag… e.g. retro" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} onChange={(e) => setTagInput(e.target.value)} data-testid="ph-tag-input" />
              <Button variant="outline" className="rounded-full shrink-0" onClick={addTag} data-testid="ph-tag-add"><Plus className="w-4 h-4" /></Button>
            </div>
            {meta.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {meta.tags.map((t) => (
                  <Badge key={t} variant="outline" className="gap-1 pr-1">#{t}
                    <button onClick={() => setMeta((m) => ({ ...m, tags: m.tags.filter((x) => x !== t) }))}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </Section>

          {/* Screenshots */}
          <Section title="Screenshots" icon={ImageIcon}>
            <div className="flex gap-2 mb-3">
              <Button variant="outline" className="rounded-full" onClick={captureShot} disabled={!!busy} data-testid="ph-capture-btn">
                {busy === "capture" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Capture from game
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="ph-upload-btn">
                {busy === "upload" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
              </Button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { uploadShot(e.target.files?.[0]); e.target.value = ""; }} />
            </div>
            {sp.screenshots.length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">No screenshots yet. Capture a frame from the live preview on the right, or upload your own (up to 8).</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="ph-screenshots">
                {sp.screenshots.map((s) => (
                  <div key={s} className="relative group rounded-xl overflow-hidden border border-[hsl(var(--border))] aspect-video">
                    <img src={imgUrl(s)} alt="screenshot" className="w-full h-full object-cover" />
                    <button onClick={() => removeShot(s)} className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="ph-remove-shot"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* RIGHT — live preview + trailer preview */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 sticky top-20">
            <div className="text-sm font-bold mb-3 flex items-center gap-2"><Camera className="w-4 h-4 text-[hsl(var(--primary))]" /> Live preview</div>
            <GameCanvas ref={canvasRef} config={game.config} compact />
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Play to the perfect moment, then “Capture from game”.</p>
            {(sp.trailer_clip_url || trailer) && (
              <div className="mt-4">
                <div className="text-sm font-bold mb-2 flex items-center gap-2"><Film className="w-4 h-4 text-[hsl(var(--primary))]" /> Trailer preview</div>
                <div className="rounded-xl overflow-hidden border border-[hsl(var(--border))] aspect-video">
                  {sp.trailer_clip_url ? (
                    <video src={imgUrl(sp.trailer_clip_url)} controls loop muted playsInline className="w-full h-full bg-black object-cover" data-testid="ph-trailer-clip-preview" />
                  ) : (
                    <iframe src={trailer} title="trailer" className="w-full h-full" allow="encrypted-media" />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={trailerPrompt} onOpenChange={setTrailerPrompt}>
        <DialogContent className="sm:max-w-md" data-testid="publish-trailer-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clapperboard className="w-5 h-5 text-amber-500" /> Add a cinematic trailer?</DialogTitle>
            <DialogDescription>
              Your game is live! 🎉 Generate a ~7-second cinematic flythrough in one tap — games with a trailer get their own spot in the Discover “🎬 Watch the trailer” rail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setTrailerPrompt(false)} data-testid="publish-trailer-later">Maybe later</Button>
            <Button onClick={() => { setTrailerPrompt(false); if (genTrailerRef.current) genTrailerRef.current(); else toast.error("Let the preview load, then use Auto-trailer."); }}
              className="rounded-full gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90" data-testid="publish-trailer-generate">
              <Clapperboard className="w-4 h-4" /> Generate my trailer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
    <h2 className="font-display font-bold mb-3 flex items-center gap-2"><Icon className="w-5 h-5 text-[hsl(var(--primary))]" /> {title}</h2>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <div><Label className="mb-1.5 block text-xs">{label}</Label>{children}</div>
);
